import { useCallback, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { generatePairs } from './squigglePairs';
import { calculateScores, detectTie, getVotingMode } from '@utils/scoreCalculator';

/**
 * Hook providing all Squiggle Game actions.
 * Host-driven state machine — the host client advances phases on timer expiry.
 * Snapshot capture happens in SquiggleActive (which has stageRef access)
 * and is passed back into the advance functions here.
 */
export function useSquiggle() {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const { user, room, game } = state;
  const advancingRef = useRef(false);
  const lastReactionRef = useRef(0);

  const memberCount = Object.keys(room.members || {}).length;
  const votingMode = getVotingMode(memberCount);

  // Host: initialise squiggle game with settings
  const initSquiggle = useCallback(async ({ squiggleDuration, transformDuration, enableVoting }) => {
    const memberIds = Object.keys(room.members || {});
    const pairs = generatePairs(memberIds);

    await firebase.updateGameState({
      pairs,
      currentPairIndex: 0,
      currentDrawerId: null,
      squiggleDuration: squiggleDuration || 10,
      transformDuration: transformDuration || 60,
      enableVoting: enableVoting ?? false,
      timerEndsAt: null,
      timerDuration: null,
      snapshots: null,
      votes: null,
      scores: null,
      winner: null,
      reactions: null,
    });
  }, [firebase, room.members]);

  // Host: start the first squiggle phase
  const startSquiggle = useCallback(async () => {
    if (!game.pairs || game.pairs.length === 0) return;
    const firstPair = game.pairs[0];
    const duration = game.squiggleDuration || 10;

    await firebase.updateGameState({
      phase: 'squiggling',
      currentPairIndex: 0,
      currentDrawerId: firstPair.squiggler,
      timerDuration: duration,
      timerEndsAt: Date.now() + (duration * 1000),
    });
  }, [firebase, game.pairs, game.squiggleDuration]);

  // Called from SquiggleActive after capturing the squiggle-only snapshot
  const advanceFromSquiggle = useCallback(async (squiggleSnapshot) => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const pairIndex = game.currentPairIndex ?? 0;
      const pair = game.pairs?.[pairIndex];
      if (!pair) return;

      const duration = game.transformDuration || 60;

      // Store the squiggle-only snapshot and transition to transforming
      await firebase.updateGameState({
        [`snapshots/${pairIndex}/squiggleOnly`]: squiggleSnapshot,
        [`snapshots/${pairIndex}/squigglerNickname`]: room.members?.[pair.squiggler]?.nickname || 'Unknown',
        [`snapshots/${pairIndex}/transformerNickname`]: room.members?.[pair.transformer]?.nickname || 'Unknown',
        phase: 'transforming',
        currentDrawerId: pair.transformer,
        timerDuration: duration,
        timerEndsAt: Date.now() + (duration * 1000),
      });

      // Clear reactions between phases
      await firebase.clearReactions();
    } finally {
      advancingRef.current = false;
    }
  }, [firebase, game.currentPairIndex, game.pairs, game.transformDuration, room.members]);

  // Called from SquiggleActive after capturing the completed snapshot
  const advanceFromTransform = useCallback(async (completedSnapshot) => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const pairIndex = game.currentPairIndex ?? 0;

      // Store the completed snapshot
      await firebase.updateGameState({
        [`snapshots/${pairIndex}/completed`]: completedSnapshot,
        phase: 'pairReveal',
        timerEndsAt: null,
        currentDrawerId: null,
      });

      // Clear reactions
      await firebase.clearReactions();
    } finally {
      advancingRef.current = false;
    }
  }, [firebase, game.currentPairIndex]);

  // Host: advance from the pair reveal — clears canvas and starts next pair or gallery
  const advanceFromReveal = useCallback(async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const nextPairIndex = (game.currentPairIndex ?? 0) + 1;
      const totalPairs = game.pairs?.length || 0;

      // Clear canvas between pairs
      await firebase.clearAllStrokes();
      dispatch({ type: 'CLEAR_ALL_STROKES' });

      if (nextPairIndex >= totalPairs) {
        // All pairs done — go to gallery
        await firebase.updateGameState({
          phase: 'gallery',
          currentPairIndex: nextPairIndex - 1,
          currentDrawerId: null,
          timerEndsAt: null,
        });
      } else {
        // Start next pair's squiggle phase
        const nextPair = game.pairs[nextPairIndex];
        const duration = game.squiggleDuration || 10;

        await firebase.updateGameState({
          phase: 'squiggling',
          currentPairIndex: nextPairIndex,
          currentDrawerId: nextPair.squiggler,
          timerDuration: duration,
          timerEndsAt: Date.now() + (duration * 1000),
        });
      }
    } finally {
      advancingRef.current = false;
    }
  }, [firebase, dispatch, game.currentPairIndex, game.pairs, game.squiggleDuration]);

  // Host-driven pairReveal auto-advance (4 seconds)
  useEffect(() => {
    if (!user.isHost || game.phase !== 'pairReveal') return;
    const timeout = setTimeout(advanceFromReveal, 4000);
    return () => clearTimeout(timeout);
  }, [user.isHost, game.phase, advanceFromReveal]);

  // Host: advance to voting phase
  const startVoting = useCallback(async () => {
    await firebase.updateGameState({
      phase: 'voting',
      timerEndsAt: null,
      currentDrawerId: null,
    });
  }, [firebase]);

  // Player: submit a vote for a specific pair
  const submitVoteForPair = useCallback(async (pairIndex, score) => {
    await firebase.submitVote(user.id, String(pairIndex), score);
  }, [firebase, user.id]);

  // Player: mark voting complete
  const finishVoting = useCallback(async () => {
    await firebase.markVotingComplete(user.id);
  }, [firebase, user.id]);

  // Host: calculate results from votes
  const calculateResults = useCallback(async () => {
    // Build submissions-like object keyed by pairIndex for scoring
    const submissions = {};
    const snapshots = game.snapshots || {};
    for (const [pairIdx, snap] of Object.entries(snapshots)) {
      if (snap.completed) {
        submissions[pairIdx] = {
          imageData: snap.completed,
          nickname: snap.transformerNickname || 'Unknown',
        };
      }
    }

    const scores = calculateScores(game.votes, submissions, votingMode);
    const tied = detectTie(scores);

    const updates = { phase: 'results', scores };

    if (tied) {
      updates.winner = null;
    } else {
      const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
      updates.winner = sorted[0]?.[0] ?? null;
    }

    await firebase.updateGameState(updates);
  }, [firebase, game.votes, game.snapshots, votingMode]);

  // Player: send a reaction (throttled to 2s)
  const sendReaction = useCallback(async (emoji) => {
    const now = Date.now();
    if (now - lastReactionRef.current < 2000) return;
    lastReactionRef.current = now;
    await firebase.pushReaction(emoji, user.id);
  }, [firebase, user.id]);

  // Host: play again with fresh pairs
  const playAgain = useCallback(async () => {
    await firebase.clearAllStrokes();
    dispatch({ type: 'CLEAR_ALL_STROKES' });

    const memberIds = Object.keys(room.members || {});
    const pairs = generatePairs(memberIds);

    await firebase.updateGameState({
      phase: 'lobby',
      pairs,
      currentPairIndex: 0,
      currentDrawerId: null,
      timerEndsAt: null,
      timerDuration: null,
      snapshots: null,
      votes: null,
      scores: null,
      winner: null,
      reactions: null,
    });
  }, [firebase, dispatch, room.members]);

  // Host: end game entirely
  const endGame = useCallback(async () => {
    await firebase.clearGame();
    dispatch({ type: 'CLEAR_GAME' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Game ended — back to free draw' } });
  }, [firebase, dispatch]);

  return {
    // State
    votingMode,
    memberCount,
    // Actions
    initSquiggle,
    startSquiggle,
    advanceFromSquiggle,
    advanceFromTransform,
    advanceFromReveal,
    startVoting,
    submitVoteForPair,
    finishVoting,
    calculateResults,
    sendReaction,
    playAgain,
    endGame,
  };
}
